<template>
    <div :style="{ width: $vuetify.display.smAndDown ? '80vw' : '40vw' }">
        <div>{{ title }}</div>
        <v-form v-model="valid">
            <v-row no-gutters class="mt-2">
                <v-col cols="7" class="pr-1">
                    <v-row
                        no-gutters
                        style="border: 1px #ededed solid; border-radius: 15px"
                        class="pa-2"
                    >
                        <v-col cols="12">
                            <v-select-filled
                                v-model="bankAccountId"
                                :items="filteredBankAccounts"
                                item-title="name"
                                item-value="id"
                                :label="
                                    paymentType === 'CUSTOMER'
                                        ? 'Compte à créditer'
                                        : 'Compte à débiter'
                                "
                                :readonly="readOnly"
                                :mandatory="true"
                                hide-details="auto"
                                :rules="[
                                    (v) =>
                                        paymentType === 'PAYMENT_CREDIT' ||
                                        !!v ||
                                        'Compte bancaire requis',
                                ]"
                                density="compact"
                            />
                        </v-col>
                        <v-col class="pt-2" cols="12" v-if="paymentType !== 'CUSTOMER'">
                            <v-combobox-filled
                                v-model="iban"
                                label="IBAN bénéficiaire"
                                :rules="[(v) => isIbanOK || 'Mauvais format']"
                                hide-details="auto"
                                :items="thirdCompanyData.ibans"
                                item-title="iban"
                                item-value="iban"
                                v-if="paymentType !== 'PAYMENT_CREDIT'"
                                :readonly="readOnly"
                            />
                            <v-text-field-filled
                                value="bobbee"
                                label="Bénéficiaire"
                                hide-details="auto"
                                readonly
                                v-else
                            />
                        </v-col>
                        <v-col cols="12" class="pt-2" v-if="websites.length">
                            <v-select-filled
                                v-model="budgetInsightWebsiteField"
                                :items="websites"
                                item-title="label"
                                item-value="value"
                                :label="websiteFieldLabel"
                                hide-details="auto"
                                :readonly="readOnly"
                            />
                        </v-col>
                    </v-row>
                </v-col>
                <v-col cols="5" class="pl-1">
                    <v-row
                        style="border: 1px #ededed solid; border-radius: 15px"
                        class="pa-2"
                        no-gutters
                    >
                        <v-col cols="12">
                            <v-text-field-filled
                                v-model="description"
                                label="Motif du paiement"
                                hide-details="auto"
                                :rules="[(v) => !!v || $t('settings.itemRequired')]"
                                :readonly="readOnly"
                                density="compact"
                            />
                        </v-col>
                        <v-col cols="12" class="py-2">
                            <v-select-filled
                                v-model="paymentCreditCount"
                                :items="[10, 25, 50, 100, 200, 400, 800]"
                                :label="'Nombre de crédits'"
                                v-if="paymentType === 'PAYMENT_CREDIT'"
                                :readonly="readOnly"
                                density="compact"
                            />
                        </v-col>
                        <v-col cols="12">
                            <v-divider />
                        </v-col>
                        <v-col cols="12" class="d-flex justify-space-between py-2">
                            <div class="title">Total</div>
                            <div class="title">{{ formatCurrency(amount) }}</div>
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>
        </v-form>
        <v-row no-gutters class="mt-2" v-if="!withoutCredits">
            <v-col class="pl-2 d-flex align-center" cols="9">
                <a :style="readOnly ? 'color:#EC6D56; font-weight:bold' : ''"
                    >Nombre de crédit(s) de paiement restant : {{ availablePaymentCreditCount }}</a
                >
            </v-col>
            <v-col cols="3" class="py-0 d-flex justify-end">
                <v-btn
                    color="red"
                    elevation="0"
                    v-if="availablePaymentCreditCount <= 0 && paymentType !== 'PAYMENT_CREDIT'"
                    @click="setPaymentCreditMode"
                    size="small"
                >
                    Ajouter des crédits
                </v-btn>
            </v-col>
        </v-row>
    </div>
</template>

<script lang="ts">
import gql from "graphql-tag";
import { Apollo } from "@/decorators";

import BUDGETINSIGHT_BANK_ACCOUNTS from "@/graphql/bank/BudgetInsightBankAccounts.gql";
import THIRDPARTY_COMPANY_DATA from "@/graphql/invoice/ThirdPartyCompanyData.gql";

import { connectBudgetInsightPay } from "@/services/bankServices";
import { newCommonFormatCurrency } from "../../../../backend/src/common/services/mathServices";
import { Component, Prop, Watch, Vue, toNative } from "vue-facing-decorator";

@Component
export class Pay extends Vue {
    // @Prop() value: boolean

    @Prop({ default: "" }) readonly companyId: string;

    @Prop({ default: "" }) readonly inPaymentType: string;
    @Prop({ default: "" }) readonly accountableAccountName: string;

    @Prop({ default: "" }) readonly thirdPartyName: string;
    @Prop({ default: "" }) readonly thirdPartyUserId: string;
    @Prop({ default: "" }) readonly thirdPartyCompanyId: string;

    @Prop({ default: false }) readonly fromPaymentManagementToGenerateEmail: boolean;
    @Prop({ default: false }) readonly withoutCredits: boolean;

    @Prop({ default: null }) accountingLines: any;

    @Prop({ default: null }) actions: any;

    // amount_ = 0

    bankAccountId = "";
    description: string = "";

    iban: any = "";

    valid = false;
    loading = false;

    paymentCreditCount = 10;
    internalPaymentType = "";

    get readOnly() {
        if (this.paymentType === "CUSTOMER") {
            return false;
        }
        return this.paymentType !== "PAYMENT_CREDIT" && this.availablePaymentCreditCount <= 0;
    }

    forbiddenCharacters = {
        é: "e",
        è: "e",
        ê: "e",
        ë: "e",
        // 'é':'E', 'è':'E',
        Ê: "E",
        Ë: "E",
        ç: "c",
        à: "a",
        â: "a",
        ä: "a",
        Â: "A",
        Ä: "A",
        ù: "u",
        ü: "u",
        û: "u",
        Û: "U",
        Ü: "U",
        î: "i",
        ï: "i",
        Î: "I",
        Ï: "I",
        ô: "o",
        ö: "o",
    };

    website = "";

    get company() {
        return this.$store.getters.getCompany(this.companyId);
    }

    get availablePaymentCreditCount() {
        if (this.company?.profile) {
            return this.company.profile.paymentCreditCount;
        }
        return 0;
    }

    get amount() {
        if (this.paymentType === "PAYMENT_CREDIT") {
            return -this.paymentCreditCount * 0.25 * 1.2;
        }
        let total = 0;
        for (const al of this.accountingLines) {
            const amountSigned = al.amountSigned > 0 ? al.amountSigned : 0;
            total += al.personalisedAmountSigned ? al.personalisedAmountSigned : amountSigned;
        }
        return total;
    }

    formatCurrency(amount) {
        return newCommonFormatCurrency(amount);
    }

    // set amount(value) {
    //     this.amount_ = value
    // }

    get isIbanOK() {
        return this.iban?.iban?.length >= 14 && this.iban.iban.length <= 34;
    }

    get globalValid() {
        if (
            !this.availablePaymentCreditCount &&
            this.paymentType !== "PAYMENT_CREDIT" &&
            !this.withoutCredits
        )
            return false;
        if (!this.valid) return false;
        if (!this.bankAccountId && this.paymentType !== "PAYMENT_CREDIT") return false;
        if (!this.paymentType)
            if (
                this.paymentType !== "PAYMENT_CREDIT" &&
                !this.thirdPartyCompanyId &&
                !this.thirdPartyUserId
            )
                return false;
        if (this.websites.length && !this.budgetInsightWebsiteField) return false;
        return true;
    }

    get bankAccount(): any {
        return this.filteredBankAccounts.find((fba) => fba.id === this.bankAccountId) || null;
    }

    get budgetInsightWebsiteField() {
        if (this.bankAccount) {
            return this.bankAccount.budgetInsightWebsiteField || "";
        }
        return "";
    }

    set budgetInsightWebsiteField(value) {
        if (this.bankAccount) {
            this.bankAccount.budgetInsightWebsiteField = value;
        }
    }

    setPaymentCreditMode() {
        this.internalPaymentType = "PAYMENT_CREDIT";
    }

    cleanDescription() {
        let description = this.description;
        for (const c in this.forbiddenCharacters) {
            const regexp = new RegExp(c, "g");
            description = description.replace(regexp, this.forbiddenCharacters[c]);
        }
        return description;
    }

    get title(): string {
        let title = this.$t("payment.title." + this.paymentType).toString();
        if (!title) {
            title = "Paiement";
        }
        return title;
    }

    get paymentType() {
        if (this.internalPaymentType) {
            return this.internalPaymentType;
        }
        if (this.inPaymentType) {
            return this.inPaymentType;
        }
        if (this.accountableAccountName) {
            if (this.accountableAccountName.startsWith("401")) {
                return "SUPPLIER";
            }
            if (this.accountableAccountName.startsWith("411")) {
                return "CUSTOMER";
            }
            if (
                this.accountableAccountName.startsWith("425") ||
                this.accountableAccountName.startsWith("4671")
            ) {
                return "EXPENSE_REPORT";
            }
            if (this.accountableAccountName.startsWith("421")) {
                return "SALARY";
            }
        }
        return "";
    }

    @Watch("thirdPartyUserId")
    @Watch("thirdPartyCompanyId")
    onThirdPartyChanged() {
        if (this.thirdPartyName) {
            this.description = "Règlement ";
            if (this.paymentType === "EXPENSE_REPORT") {
                this.description = "Remboursement à ";
            }
            this.description += this.thirdPartyName;
        } else {
            this.description = this.title;
        }
    }

    async startPayment() {
        this.loading = true;
        if (this.globalValid) {
            await connectBudgetInsightPay(
                this.$apollo,
                this.$route.fullPath,
                this.companyId,
                this.bankAccountId,
                this.budgetInsightWebsiteField,
                this.cleanDescription(),
                this.paymentType,
                this.accountableAccountName,
                this.thirdPartyUserId,
                this.thirdPartyCompanyId,
                this.iban.iban,
                this.amount,
                this.accountingLines,
                this.paymentCreditCount,
            );
        }
        this.loading = false;
        // this.dialog = false
    }

    beforeMount() {
        this.onThirdPartyChanged();

        if (this.actions) {
            this.actions.action.title = this.fromPaymentManagementToGenerateEmail
                ? "Suivant"
                : "Démarrer le paiement";
            this.actions.action.function = this.fromPaymentManagementToGenerateEmail
                ? this.emitMail
                : this.startPayment;
            this.actions.action.loading = () => {
                return this.loading;
            };
            this.actions.action.disabled = () => {
                return !this.globalValid;
            };
        }
    }

    emitMail() {
        if (this.globalValid) {
            this.$emit("getBudgetInsightOptions", {
                companyId: this.companyId,
                bankAccountId: this.bankAccountId,
                description: this.cleanDescription(),
                thirdPartyCompanyId: this.thirdPartyCompanyId,
                amountSigned: this.amount,
                paymentType: this.paymentType,
                accountingLines: this.accountingLines,
            });
        }
    }

    @Apollo({
        query: BUDGETINSIGHT_BANK_ACCOUNTS,
        variables() {
            return {
                companyId: this.companyId,
            };
        },
        skip() {
            return !this.companyId;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (data?.bankAccounts?.length) {
                    this.bankAccounts = data.bankAccounts;
                    if (this.bankAccounts.length === 1) {
                        this.bankAccountId = this.bankAccounts[0].id;
                    }
                } else {
                    this.bankAccounts = [];
                }
            }
        },
    })
    bankAccounts = [];

    @Apollo({
        query: THIRDPARTY_COMPANY_DATA,
        variables() {
            return {
                thirdPartyCompanyId: this.thirdPartyCompanyId,
            };
        },
        skip() {
            return !this.companyId || !this.thirdPartyCompanyId;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (data?.company?.thirdPartyCompanyData) {
                    // debugger;
                    this.thirdCompanyData = data.company.thirdPartyCompanyData;
                    if (!this.thirdCompanyData?.ibans) {
                        this.thirdCompanyData.ibans = [];
                        this.iban = "";
                    }
                } else {
                    this.thirdCompanyData = { ibans: [] };
                    this.iban = "";
                }
            }
        },
    })
    thirdCompanyData: any = {
        ibans: [],
    };

    @Apollo({
        query: gql`
            query ($id: ID!) {
                user(where: { id: $id }) {
                    id
                    firstname
                    lastname
                    iban
                }
            }
        `,
        variables() {
            return {
                id: this.thirdPartyUserId,
            };
        },
        skip() {
            return !this.thirdPartyUserId;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                // this.ibans = []
                if (data?.user?.iban) {
                    this.user = data.user;
                    this.iban = data.user.iban;
                } else {
                    // this.ibans = []
                    this.iban = "";
                }
            }
        },
    })
    user = {};

    get filteredBankAccounts() {
        if (this.paymentType === "PAYMENT_CREDIT") {
            const bankAccounts = [{ id: "", name: "Pas dans la liste" }];
            bankAccounts.push(...this.bankAccounts);
            return bankAccounts;
        }

        return this.bankAccounts;
    }

    get profileId() {
        return this.$store.state.selected_company?.profile?.id;
    }

    get websiteField() {
        if (this.bankAccountId) {
            if (
                this.bankAccount &&
                this.bankAccount.connector &&
                this.bankAccount.connector.fields
            ) {
                const field = this.bankAccount.connector.fields.find((f) => f.name === "website");
                if (field && field.connector_sources.indexOf("openapi") !== -1) {
                    return field;
                }
            }
        }
        return undefined;
    }

    get websites() {
        if (this.websiteField) {
            return this.websiteField.values;
        }
        return [];
    }

    get websiteFieldLabel() {
        if (this.websiteField) {
            return this.websiteField.label;
        }
        return "";
    }
}

export default toNative(Pay);
</script>
